import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, ArrowUpDown } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money } from "@/lib/format";
import { PageHeader, Panel, Empty } from "@/components/Shell";

const blank = {
  name: "", sku: "", type: "inventory", unit_price: 0, cost_price: 0,
  quantity: 0, reorder_level: 0, vat_applicable: true, description: "",
};

export default function Products() {
  const { canWrite } = useAuth();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(blank);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [adjust, setAdjust] = useState(null);
  const [adjustQty, setAdjustQty] = useState("");

  const load = () => api.get("/products").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const num = (k) => (e) => setForm({ ...form, [k]: Number(e.target.value) });

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/products/${editing}`, form);
      else await api.post("/products", form);
      toast.success("Product saved");
      setOpen(false); setEditing(null); setForm(blank); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/products/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const submitAdjust = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/products/${adjust.id}/adjust`, { quantity: Number(adjustQty), reason: "Manual adjustment" });
      toast.success("Stock adjusted");
      setAdjust(null); setAdjustQty(""); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div data-testid="products-page">
      <PageHeader title="Products & Inventory" subtitle="Stock items and services with UGX pricing and reorder alerts."
        action={canWrite && (
          <button data-testid="add-product-button" onClick={() => { setForm(blank); setEditing(null); setOpen(true); }}
            className="flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">
            <Plus size={16} /> New product
          </button>
        )} />

      <Panel title={`${rows.length} items`}>
        {rows.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="products-table">
              <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="px-5 py-2.5">Item</th><th className="px-5 py-2.5">SKU</th>
                  <th className="px-5 py-2.5">Type</th><th className="px-5 py-2.5 text-right">Cost</th>
                  <th className="px-5 py-2.5 text-right">Price</th><th className="px-5 py-2.5 text-right">On hand</th>
                  <th className="px-5 py-2.5 text-right">Actions</th></tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                    <td className="px-5 py-3 font-medium text-slate-900">{p.name}</td>
                    <td className="px-5 py-3 font-mono text-xs text-slate-500">{p.sku || "—"}</td>
                    <td className="px-5 py-3 capitalize text-slate-600">{p.type}</td>
                    <td className="px-5 py-3 text-right tabular-nums">{money(p.cost_price)}</td>
                    <td className="px-5 py-3 text-right tabular-nums font-medium">{money(p.unit_price)}</td>
                    <td className={`px-5 py-3 text-right tabular-nums font-semibold ${p.type === "inventory" && p.quantity <= p.reorder_level ? "text-rose-600" : "text-slate-800"}`}>
                      {p.type === "inventory" ? p.quantity : "—"}
                    </td>
                    <td className="px-5 py-3 text-right">
                      {canWrite && (
                        <div className="flex justify-end gap-2">
                          {p.type === "inventory" && (
                            <button data-testid="adjust-stock-button" onClick={() => setAdjust(p)} className="text-slate-400 hover:text-sky-600" aria-label="Adjust stock"><ArrowUpDown size={15} /></button>
                          )}
                          <button data-testid="edit-product-button" onClick={() => { setForm({ ...blank, ...p }); setEditing(p.id); setOpen(true); }}
                            className="text-slate-400 hover:text-emerald-600" aria-label="Edit product"><Pencil size={15} /></button>
                          <button data-testid="delete-product-button" onClick={() => remove(p.id)}
                            className="text-slate-400 hover:text-rose-600" aria-label="Delete product"><Trash2 size={15} /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <Empty message="No products or services yet." />}
      </Panel>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
          <div className="w-full max-w-lg rounded-lg border border-slate-200 bg-white shadow-md" data-testid="product-dialog">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
              <h3 className="font-display text-base font-bold text-slate-900">{editing ? "Edit product" : "New product"}</h3>
              <button data-testid="close-product-dialog" onClick={() => setOpen(false)} className="text-slate-400"><X size={18} /></button>
            </div>
            <form onSubmit={submit} className="grid grid-cols-2 gap-4 p-5">
              <div className="col-span-2">
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Name</label>
                <input data-testid="product-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500" />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">SKU</label>
                <input data-testid="product-sku-input" value={form.sku || ""} onChange={(e) => setForm({ ...form, sku: e.target.value })}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500" />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Type</label>
                <select data-testid="product-type-select" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500">
                  <option value="inventory">Inventory</option>
                  <option value="service">Service</option>
                </select>
              </div>
              {[["Cost price (UGX)", "cost_price"], ["Selling price (UGX)", "unit_price"],
                ["Quantity on hand", "quantity"], ["Reorder level", "reorder_level"]].map(([label, key]) => (
                <div key={key}>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">{label}</label>
                  <input data-testid={`product-${key}-input`} type="number" step="any" value={form[key]} onChange={num(key)}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500" />
                </div>
              ))}
              <button data-testid="save-product-button" className="col-span-2 rounded-md bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700">Save product</button>
            </form>
          </div>
        </div>
      )}

      {adjust && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
          <form onSubmit={submitAdjust} className="w-full max-w-sm rounded-lg border border-slate-200 bg-white p-5 shadow-md" data-testid="adjust-stock-dialog">
            <h3 className="font-display text-base font-bold text-slate-900">Adjust stock — {adjust.name}</h3>
            <p className="mt-1 text-sm text-slate-500">Current on hand: {adjust.quantity}. Use a negative number to reduce.</p>
            <input data-testid="adjust-quantity-input" type="number" step="any" required value={adjustQty} onChange={(e) => setAdjustQty(e.target.value)}
              className="mt-4 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500" />
            <div className="mt-4 flex gap-2">
              <button data-testid="save-adjust-button" className="flex-1 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">Apply</button>
              <button type="button" data-testid="cancel-adjust-button" onClick={() => setAdjust(null)}
                className="flex-1 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
